import { useContext, useEffect, useState } from "react";
import FormContext from "../../context/form-context";
import { createFormData, encryptData, filterByFormType, getExistingFileName, getFileExtension } from "../../utils/utilities";
import { setAlert,  setFormSignatories, setSharedForms,  setSupportingDocs,  setTemplatesField } from "../../state/action";
import { uploadFile } from "../../services/real-star-api/file/upload-file";
import {  constructFormData_toPDF, downloadBlobFile, downloadFileByURL, findTemplate, validateFile, viewPDFFile } from "./pdfUtil";
import { checkIfFileExists } from "../../services/real-star-api/file/file-checker";
import { ALERT_TYPE } from "../../constants/alert-type";
import { updateSubmittedForm } from "../../services/firebase/submittedForms/tb_submittedForms";
import dayjs from "dayjs";
import { mutualAgreement} from "../FormPage/specialFields";
import { MODE } from "../../constants/form-mode";
import { PDFDocument } from "pdf-lib";
import { baseUrl } from "../../config/app-config";
import useAuth from "../../hooks/useAuth";

const useViewPDFPage = () => {
    const { appState, dispatch } = useContext(FormContext)
    const { auth } = useAuth()
    const {
        formState,
        selectedTemplates,
        formTemplatesList,
        formType,
        formSignatories,
        userDetails,
        formInfo,
        agentDetails,
        supportingDocuments,
        sharedForms
    } = appState
    const {transactionID , mode , status} = appState.formInfo

    const [allPDFTemplates, setAllPDFTemplates] = useState([]);
    const [ disabledForms , setDisabledForms] = useState([]);
    const [ templateFieldDisable, setTemplateFieldDisable] = useState(false)
    const [pageAlert , setPageAlert] = useState({show: false, severity: "success", message: ""})
    const mainValues = formState.mainValues
    const agentValues = agentDetails
    const [requiredForms, setRequiredForms] = useState([]);
    const [checklistForm, setChecklistForm] = useState([]);
    
    useEffect(() => {
        const filteredTemplates = filterByFormType(formTemplatesList, formType)
        //sorted by name
        setAllPDFTemplates( filteredTemplates.sort((a, b) => a.FormName.localeCompare(b.FormName)))

        checkMutualAgreementField();

        const requiredForms = filteredTemplates.filter(item => item.IsRequired).map(idx => idx.FormName)
        setRequiredForms(requiredForms);

        if (selectedTemplates.length < 1) { //if no selected yet.
            dispatch(setTemplatesField(requiredForms));
        }

        dispatch(setSharedForms(sharedForms ));
        validateChecklistForms(filteredTemplates);

    }, [])
        
    useEffect( () => {
        setTemplateFieldDisable(formInfo.mode === MODE.CLOSED);
    },[formInfo])

    useEffect( () => {
        if(selectedTemplates.length > 0){
            const newSelectedTemplates = selectedTemplates.filter( i => !disabledForms.includes(i) && !checklistForm.includes(i)); //remove from selected temlates
            dispatch(setTemplatesField([...checklistForm, ...newSelectedTemplates ])) //remove disabled form from the selected templates , re-attached the checklist form
        }
    },[disabledForms, checklistForm])

    const btnAction = {
        handleDownloadFile: async (file) => {
            
            try {
                console.log('Processing the file ...')
    
                //without space // default to .pdf
                const fileExtension = getFileExtension(file?.FormName);
                const newFileName = file?.FormName;

                const existingFileName = getExistingFileName(file?.FormName)
                const existingFileURL = await checkIfFileExists(transactionID, existingFileName);
    
                // from upload
                if(existingFileURL){
                    const cacheBustingURL = `${existingFileURL}?t=${new Date().getTime()}`
                    const response = await fetch(cacheBustingURL);
    
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
    
                    try {
                        // generate uploaded PDF
                        // const pdfblob = response.blob();
                        const templateURL = await response?.url;
                        if(fileExtension === "" || fileExtension === "pdf"){
                            const fieldSignatories = getSignatories(file)
                            const blob = await constructFormData_toPDF(templateURL,fieldSignatories, mainValues, agentValues); //plot the form data
                            // download the existing file.
                            await downloadBlobFile(blob, newFileName)
                        }else{
                            // for supporting docs
                            await downloadFileByURL(response?.url, newFileName)
                        }

                        console.log('with existing file') 
                    } catch (error) {
                        console.log(error)
                        dispatch(setAlert({
                            show: true,
                            title: ALERT_TYPE.ERROR.toUpperCase(),
                            body: 'Error downloading the PDF.',
                        }))
                    }
                    
                }
                else{
                    // generate initial PDF
                    try {
                    const templateLocalPath = findTemplate(file , allPDFTemplates)
                    const fieldSignatories = getSignatories(file)
                    const pdfBlob = await constructFormData_toPDF(templateLocalPath, fieldSignatories, mainValues, agentValues); //plot the form data
                    await downloadBlobFile(pdfBlob, newFileName)
                    
                    console.log('no existing file') 
                    } catch (error) {
                        console.log(error)
                        dispatch(setAlert({
                            show: true,
                            title: ALERT_TYPE.ERROR.toUpperCase(),
                            body: 'Error downloading the PDF.',
                        }))
                    }
                }

                console.log('Download completed...')
            } catch (error) { 
                dispatch(setAlert({
                    show: true,
                    title: 'PDF PROCESSING ERROR',
                    body: 'The PDF does not exist. Please check the Local Path.',
                }))
            }
        },

        handleUploadFile: async (targetFile , file , isOverwriteSignature) => {
            
            const userEncryptedID = userDetails?.encrypted_id
            const newFileName = file?.FormName?.replace(/\s+/g, ''); //remove spacing to avoid error in uploading..
           
            const validatedFile = validateFile(targetFile);
        
            if (validatedFile.valid) {
                
                const formData = createFormData(targetFile , newFileName, transactionID)
    
                try {
                    dispatch(setAlert({
                        show: true,
                        title: 'FILE UPLOADING.',
                        body: `The file is currently uploading. <br/><br/>Kindly wait a moment. `,
                        showBtn:false
                    }))
                    const resultFile = await uploadFile(formData, userEncryptedID); // Uncomment when ready to upload
                 
                    if (resultFile) {
                        dispatch(setAlert({
                            show: true,
                            title: 'SUCCESSFULLY UPLOADED',
                            body: `<br/>Your file has been uploaded successfully!
                                 ${isOverwriteSignature ? '<br/> <br/> The signature has been reset.' : ""}
                            <br/>`,
                        }))
                        
                        if(isOverwriteSignature){
                            //put condition if SignatureOverwrite is True
                            const updatedSignatories = formSignatories.map(i => {
                                if (i.formName === file?.FormName) {
                                    return { ...i, signatoryValues: [] };
                                }
                                return i; // Return the original object unchanged
                            });

                            await resetSignatureOnUpload(updatedSignatories)
                            dispatch(setFormSignatories(updatedSignatories))
                        }
                        

                    }else{
                        dispatch(setAlert({
                            show: true,
                            title: 'ERROR: UPLOAD',
                            body: `File Upload Failed. Please try again.`,
                        }))
                    }
                } catch (err) {
                    dispatch(setAlert({
                        show: true,
                        title: 'ERROR: UPLOAD',
                        body: `File Upload Failed. Please try again.`,
                    }))
                }
            } else {
                dispatch(setAlert({
                    show: true,
                    title: 'ERROR: UPLOAD',
                    body: `${validatedFile.errMsg}`,
                }))
            }
        },

        handleShareFile: async (fileID, formName , type) => {
            const encryptedDate = encryptData(dayjs(new Date()).format('MM-DD-YYYY'))
            const customEncryptedDate = encryptedDate.replaceAll('/', 'xxx'); //to avoid conflict on router
            
            const fileLink = `${baseUrl}/public/forms/${type}/${transactionID}/${fileID}/${customEncryptedDate}`;
            const title = formName;
            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${fileLink}`)}`;
            window.open(whatsappUrl, '_blank');
        },

        handleCopyLink: async () => {
            //copying of the public link is inside the PDF Card.
            setPageAlert({...pageAlert, show: true, message: "The link has been successfully copied to your clipboard"})
            setTimeout(() => {
                setPageAlert({...pageAlert, show: false, message: ""})
            }, 1500);
        },

        handleRemoveFile: async (file) => {
            const updateSuppDocs = supportingDocuments.filter( doc => doc.FormName !== file.FormName)
            
            dispatch(setSupportingDocs( updateSuppDocs  ))

            const newData = {
                SupportingDocuments: updateSuppDocs,
                ModifiedBy: auth.id === appState.agentDetails?.id ? appState.agentDetails : appState.userDetails,   
            }
            await updateSubmittedForm(transactionID, newData);
        },

        handleMarkSharing: async (file) => {
            if(sharedForms.includes(file.FormName)){
                dispatch(setSharedForms(sharedForms.filter( i => i !== file.FormName)))
            }else{
                dispatch(setSharedForms([...sharedForms, file.FormName]))
            }
        },

        handleUploadSupportingDocs: async (files) => {
            if (files.length > 0) {
                const newDocs = [];
                let uploadPromises = []; // Array to store upload promises
                dispatch(setAlert({
                    show: true,
                    title: 'Uploading File',
                    body: `The file are currently uploading. <br/>Kindly wait a moment. <br/><br/>`,
                    showBtn: false
                }));

                for (const file of files) {
                    const validatedFile = validateFile(file);

                    if (validatedFile.valid) {

                        if (supportingDocuments.find(doc => doc.FormName === file.name)) {
                            dispatch(setAlert({
                                show: true,
                                title: 'UPLOAD',
                                body: `The file '${file.name}' already exists.`,
                            }));
                        } else {

                            newDocs.push({
                                FormName: file.name,
                                FileType: file.type,
                                FileExtension: getFileExtension(file.name),
                            });

                            // Push the upload promise to the array
                            uploadPromises.push(uploadSupportingDocs(file));
                        }

                    } else {
                        dispatch(setAlert({
                            show: true,
                            title: 'ERROR: UPLOAD',
                            body: `${validatedFile.errMsg}`,
                        }));
                    }
                }

                // Wait for all uploads to complete
                try {
                    await Promise.all(uploadPromises); // Wait for all uploads to finish

                    if (newDocs.length > 0) {

                        dispatch(setSupportingDocs([...supportingDocuments, ...newDocs]))


                        const newData = {
                            SupportingDocuments: [...supportingDocuments, ...newDocs],
                            ModifiedBy: auth.id === appState.agentDetails?.id ? appState.agentDetails : appState.userDetails,  
                        }
                        await updateSubmittedForm(transactionID, newData);

                        dispatch(setAlert({
                            show: true,
                            title: 'SUCCESSFULLY UPLOADED',
                            body: `<br/>Your files have been uploaded successfully! <br/>`,
                        }));
                    }
                } catch (uploadError) {
                    console.error('Error during file uploads:', uploadError);
                    dispatch(setAlert({
                        show: true,
                        title: 'Upload Error',
                        body: `Some files failed to upload. Please try again.`,
                    }));
                }
            }
        },
        handleViewFile : async (file) => {

            try {
                console.log('Processing the file ...')
    
                const fileExtension = getFileExtension(file?.FormName);
                const newFileName = file?.FormName;

               const existingFileName = getExistingFileName(file?.FormName)
               const existingFileURL = await checkIfFileExists(transactionID, existingFileName);
    
                // from upload
                if (existingFileURL) {
                    const cacheBustingURL = `${existingFileURL}?t=${new Date().getTime()}`
                    const response = await fetch(cacheBustingURL);
    
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
    
                    try {

                        const templateURL = await response?.url;
                        if(fileExtension === "" || fileExtension === "pdf"){
                            const fieldSignatories = getSignatories(file)
                            const blob = await constructFormData_toPDF(templateURL,fieldSignatories, mainValues, agentValues); //plot the form data
                            // view the existing file.
                            await viewPDFFile(blob, newFileName)
                        }
                        //no View function for supp docs
                        // else{
                        //     // for supporting docs
                        //     await viewPDFFile(response?.url, newFileName)
                        // }

                        console.log('with existing file') 

                        // const templateURL = await response?.url;
                        
                        // if(fileExtension === "" || fileExtension === "pdf"){
                        //     window.open(`${templateURL}#toolbar=0&navpanes=0&scrollbar=0`, '_blank', 'width=800,height=800');
                        // }else{
                        //     // for supporting docs
                        //     // await downloadFileByURL(templateURL, newFileName)
                        //     window.open(`${templateURL}#toolbar=0&navpanes=0&scrollbar=0`, '_blank', 'width=800,height=800');
                        // }
                        
                        // console.log('with uploaded file')
                    } catch (error) {
                        console.log(error)
                        dispatch(setAlert({
                            show: true,
                            title: ALERT_TYPE.ERROR.toUpperCase(),
                            body: 'Error downloading the PDF.',
                        }))
                    }
    
                }
                else {
                    // generate initial PDF
                    try {
                        const templateLocalPath = findTemplate(file , allPDFTemplates)
                        const fieldSignatories = getSignatories(file)
                        const pdfBlob = await constructFormData_toPDF(templateLocalPath, fieldSignatories, mainValues, agentValues); //plot the form data
                        await viewPDFFile(pdfBlob, newFileName)
                        
                        console.log('no existing file') 
                        } catch (error) {
                            console.log(error)
                            dispatch(setAlert({
                                show: true,
                                title: ALERT_TYPE.ERROR.toUpperCase(),
                                body: 'Error downloading the PDF.',
                            }))
                        }
                }
                console.log('Completed...')
    
            } catch (error) {
                dispatch(setAlert({
                    show: true,
                    title: 'Error processing the file',
                    body: 'The PDF does not exist. Please check the Local Path',
                }))
            }
        }
        
    }

    const getSignatories = (file) => {
     
        const signatories = formSignatories?.find( form=> form.formId === file.Id);
        const signatoryValues = signatories?.signatoryValues || []
      
        return signatoryValues?.reduce((acc, item) => {
            acc[item.field + 'Signature'] = item[item.field + 'Signature'];
            acc[item.field + 'SignedDate'] = item[item.field + 'SignedDate'];
            return acc;
        }, {});
    }

    const signButtonLabel = (file) => {
        //get signatory values
        const signatories = formSignatories?.find( form=> form.formId === file.Id);
        const signatoryValues = signatories?.signatoryValues || []
        
        //check if there's open signatories
        const open = signatoryValues.find( sign => !sign.signature)
        return open || signatoryValues.length < 1 ? "sign" : "signed"
    }
    const isFileExists = async (file) => {
        const existingFileName = getExistingFileName(file?.FormName)
        const existingFileURL = await checkIfFileExists(transactionID, existingFileName);
        
        return !!existingFileURL; // This will return true or false
    }

    const getPublicLink = (fileID , type) => {
        //type:
        // 0 = primary file/with template
        // 1 = supporting documents
        const encryptedDate = encryptData(dayjs(new Date()).format('MM-DD-YYYY'))
        const customEncryptedDate = encryptedDate.replaceAll('/', 'xxx'); //to avoid conflict on router
        const link = `${baseUrl}/public/forms/${type}/${transactionID}/${fileID}/${customEncryptedDate}`
        return link;
    }

    const onSelectTemplates = (val) => {
        const defaultFormVal = val.filter(idx => requiredForms.find(req => req === idx));
        
        //set what is/are not required forms
        if (defaultFormVal.length === requiredForms.length) {
            dispatch(setTemplatesField(val));
        }
        // dispatch(setFormInfo({...formInfo, withUpdates: true}))
    };

    const checkMutualAgreementField = () => {
        //if any mutual agreement is equal to YES only
        const agreed = Object.entries(mainValues).some(([key, value]) => 
            key.toLowerCase().includes(mutualAgreement) && (value === "Yes")
        );
        if(agreed){
            dispatch(setAlert({
                    show: true, 
                    title: "REMINDER", 
                    body:"Please ensure that the internal commission sharing document is attached to the supporting documents."
                }))
        }
    }
   
    const resetSignatureOnUpload = async (data) => {
        const newData = { 
            FormSignatories: data , 
            ModifiedBy: appState.agentDetails,  
        }
        await updateSubmittedForm(transactionID, newData);
    }

    const uploadSupportingDocs = async (file) => {
        const userEncryptedID = userDetails?.encrypted_id
        const newFileName = file.name?.replace(/\s+/g, '');
  
        const formData = createFormData(file , newFileName, transactionID)

        try {
            await uploadFile(formData, userEncryptedID);
        } 
        catch (err) {
            console.error('Upload error:', err);
            dispatch(setAlert({
                show: true,
                title: 'Upload Error',
                body: `File Upload Failed. Please try again.`,
            }))
        }
    }

    const validateChecklistForms = (formTemplates) => {
        const {buyerAssociate1Name_ID , sellerAssociate1Name_ID, tenantAssociate1Name_ID , landlordAssociate1Name_ID} = formState.mainValues
        const disabledTempArr = [];
        const attachedTempArr = [];
        
        const SellerForSale_PurchaseTemplate = formTemplates.find( i => i.FormName?.toLowerCase().includes("checklist ") &&  i.FormName?.toLowerCase().includes("seller") );
        const BuyerForSale_PurchaseTemplate = formTemplates.find( i => i.FormName?.toLowerCase().includes("checklist ") &&  i.FormName?.toLowerCase().includes("buyer") );
        const TenantForSale_PurchaseTemplate = formTemplates.find( i => i.FormName?.toLowerCase().includes("checklist ") &&  i.FormName?.toLowerCase().includes("tenant") );
        const LandlordForSale_PurchaseTemplate = formTemplates.find( i => i.FormName?.toLowerCase().includes("checklist ") &&  i.FormName?.toLowerCase().includes("landlord") );
        
        if(!buyerAssociate1Name_ID){
            if(BuyerForSale_PurchaseTemplate){
                disabledTempArr.push(BuyerForSale_PurchaseTemplate.FormName)
            }
        }else{
            attachedTempArr.push(BuyerForSale_PurchaseTemplate.FormName)
        }
        if(!sellerAssociate1Name_ID){
            if(SellerForSale_PurchaseTemplate){
                disabledTempArr.push(SellerForSale_PurchaseTemplate.FormName)
            }
        }else{
            attachedTempArr.push(SellerForSale_PurchaseTemplate.FormName)
        }
        if(!tenantAssociate1Name_ID){
            if(TenantForSale_PurchaseTemplate){
                disabledTempArr.push(TenantForSale_PurchaseTemplate.FormName)
            }
        }else{
            attachedTempArr.push(TenantForSale_PurchaseTemplate.FormName)
        }
        if(!landlordAssociate1Name_ID){
            if(LandlordForSale_PurchaseTemplate){
                disabledTempArr.push(LandlordForSale_PurchaseTemplate.FormName)
            }
        }else{
            attachedTempArr.push(LandlordForSale_PurchaseTemplate.FormName)
        }
        setDisabledForms(disabledTempArr)
        setChecklistForm(attachedTempArr)
    }

    return {
        // selectedForms,
        allPDFTemplates,
        btnAction,
        onSelectTemplates,
        selectedTemplates,
        sharedForms,
        templateFieldDisable,
        signButtonLabel,
        pageAlert,
        supportingDocuments,
        mode,
        status,
        getPublicLink,
        disabledForms,
        isFileExists
    }
}

export default useViewPDFPage;